import {
  UserPlus,
  LayoutTemplate,
  Radio,
  Banknote,
  type LucideIcon,
} from "lucide-react";

/** Which audience a step's tag calls out. Drives the chip styling. */
type Audience = "company" | "driver" | "both";

interface Step {
  /** 1-based ordinal shown as a large mono numeral. */
  n: number;
  icon: LucideIcon;
  title: string;
  description: string;
  /** Audience callouts shown as small chips under the description. */
  tags: { label: string; audience: Audience }[];
}

/**
 * The signup → first-booking path, written so an independent driver and a
 * transportation company both see themselves in the same flow. Static content
 * only — no fabricated metrics or customer names.
 */
const steps: Step[] = [
  {
    n: 1,
    icon: UserPlus,
    title: "Create your account & get approved",
    description:
      "Sign up as an operator or as an independent driver — same console, scoped to how you operate. New accounts are reviewed and approved before going live.",
    tags: [
      { label: "Operator", audience: "company" },
      { label: "Driver", audience: "driver" },
    ],
  },
  {
    n: 2,
    icon: LayoutTemplate,
    title: "Set up your booking page",
    description:
      "Brand your booking page and pricing. Operators invite drivers; solo drivers go live on their own.",
    tags: [
      { label: "Invite drivers", audience: "company" },
      { label: "Go solo", audience: "driver" },
    ],
  },
  {
    n: 3,
    icon: Radio,
    title: "Take reservations & dispatch",
    description:
      "Reservations land in one place. Assign and dispatch trips, or run your own jobs end to end.",
    tags: [{ label: "Live dispatch", audience: "both" }],
  },
  {
    n: 4,
    icon: Banknote,
    title: "Get paid",
    description:
      "Collect payment on every trip and reconcile it without leaving the console.",
    tags: [{ label: "Payments built in", audience: "both" }],
  },
];

// This stepper shows BOTH audiences' items at once, so the per-item audience
// tags stay NEUTRAL metadata chips rather than competing accents — a blanket
// accent remap (the page-level audience scope) would otherwise mis-color them.
// The genuine accent elements (step-node circles, the "NN steps" eyebrow
// numeral) remain `primary-*` and re-theme via that scope.
const tagStyles: Record<Audience, string> = {
  company: "border-neutral-200 bg-neutral-100 text-neutral-600",
  driver: "border-neutral-200 bg-neutral-100 text-neutral-600",
  both: "border-neutral-200 bg-neutral-100 text-neutral-600",
};

/**
 * "How it works" section for the public homepage. Lays out the path from signup
 * to first booking as a four-step dispatch-style stepper: a connector rail with
 * cobalt nodes on desktop, stacked cards on mobile. Dual-audience throughout
 * (independent drivers and transportation companies are co-equal).
 *
 * Standalone and presentational — no required props, no router, no IO. The
 * integration unit wraps this section with `id="how-it-works"`, so it sets no
 * id of its own.
 * @returns The "How it works" homepage section.
 */
export function HowItWorksSection() {
  return (
    <section
      aria-labelledby="how-it-works-heading"
      className="bg-background"
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24 lg:px-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-surface px-3 py-1 text-xs font-medium uppercase tracking-wide text-neutral-600">
            <span className="font-mono tabular-nums text-primary-700">04</span>
            steps to live
          </span>
          <h2
            id="how-it-works-heading"
            className="mt-5 text-3xl font-bold tracking-tight text-neutral-900"
          >
            From signup to your first booking
          </h2>
          <p className="mt-3 text-lg text-neutral-600">
            One path, two ways to run it. Whether you dispatch a fleet or drive
            every trip yourself, you go from account to live reservations in
            four steps.
          </p>
        </div>

        <ol className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-0">
          {steps.map((step, index) => (
            <li
              key={step.n}
              className="relative flex flex-col rounded-md border border-neutral-200 bg-surface p-5 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:pr-6"
            >
              {/* Desktop connector rail: a hairline route line linking the step
                  nodes. Hidden on the last step and on stacked (mobile) layout.
                  Decorative — the ordered list already conveys sequence. */}
              {index < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className="absolute left-11 right-0 top-5 hidden h-px bg-neutral-200 lg:block"
                />
              )}

              <div className="flex items-center gap-3 lg:block">
                {/* Step node: large mono ordinal in a cobalt-ringed marker that
                    sits on the connector rail. */}
                <span className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-surface font-mono text-lg font-semibold tabular-nums text-primary-700">
                  {step.n}
                </span>
                <step.icon
                  className="h-5 w-5 text-neutral-400 lg:mt-5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              </div>

              <h3 className="mt-4 text-base font-semibold text-neutral-900">
                {step.title}
              </h3>
              <p className="mt-1 text-sm text-neutral-600">
                {step.description}
              </p>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {step.tags.map((tag) => (
                  <span
                    key={tag.label}
                    className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium ${tagStyles[tag.audience]}`}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
